// Card of one open match ("Falta un jugador") inside Historial's "Canchas" tab (T15).
// Reuses the hist-card layout of court reservations. Actions come from `match.me` (back-computed):
//  - me.canLeave  -> "Salir del partido"; me.canCancel -> "Cancelar partido" (creator), both with inline confirmation.
//  - otherwise the back's `leaveBlockedReason` is shown in Spanish (not for cancelled matches or when already left).
// The parent owns the API calls: `onLeave` / `onCancel` return a promise; a rejection is shown inline and the
// parent refreshes the list on success.
import React, { useState } from "react";
import { Spinner } from "react-bootstrap";
import dayjs from "dayjs";
import RIcon from "./ReservasIcons";
import { SPORT_LABELS } from "../../../constants/courtDomain";
import {
  MATCH_STATE_LABELS,
  leaveBlockedMessage,
  manageActionErrorMessage,
  matchViewState,
  missingLabel,
} from "../../canchas/openMatchesModel";

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1514933651103-005eec06c04b?auto=format&fit=crop&w=800&q=80";

/** Label + status-pill class for the card; "Saliste" wins over the match status. */
export const openMatchPill = (match) => {
  if (match?.me?.left) return { cls: "cancel", label: "Saliste" };
  const view = matchViewState(match);
  if (view === "open") return { cls: "upcoming", label: missingLabel(match.missing) };
  return {
    cls: view === "cancelled" ? "cancel" : view === "closed" ? "past" : "upcoming",
    label: MATCH_STATE_LABELS[view] || view,
  };
};

const OpenMatchHistoryCard = ({ match, onOpen, onLeave, onCancel }) => {
  const me = match.me || {};
  // "leave" | "cancel" | null: which inline confirmation is open.
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const pill = openMatchPill(match);
  const date = match.date ? dayjs(match.date) : null;
  const showBlocked = !me.canLeave && !me.canCancel && !me.left && match.status !== "cancelled" && me.leaveBlockedReason;

  const run = async (action, fallback) => {
    try {
      setBusy(true);
      setError("");
      await action(match._id);
      setConfirming(null);
    } catch (err) {
      setError(manageActionErrorMessage(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  const stop = (e) => e.stopPropagation();

  return (
    <div className="hist-card" data-testid="open-match-history-card" onClick={() => onOpen(match._id)}>
      <div className="hist-thumb">
        <img src={FALLBACK_IMAGE} alt={match.clubName} />
        {date && (
          <div className="hist-date-badge">
            <div className="d">{date.format("DD")}</div>
            <div className="m">{date.format("MMM").toUpperCase()}</div>
          </div>
        )}
      </div>
      <div className="hist-mid">
        <div className="hn">{match.clubName}</div>
        <div className="hmeta">
          {match.date && <span className="hm-left"><RIcon name="cal" width={14} height={14} /> {date.format("DD/MM/YYYY")}</span>}
          {match.slotStart && <span className="hm-right"><RIcon name="clock" width={14} height={14} /> {match.slotStart} hs</span>}
          {match.courtName && <span className="hm-left"><RIcon name="court" width={14} height={14} /> {match.courtName}</span>}
          {match.sport && <span className="hm-right"><RIcon name="ball" width={14} height={14} /> {SPORT_LABELS[match.sport] || match.sport}</span>}
        </div>
      </div>
      <div className="hist-side" onClick={stop}>
        <span className="status-pill upcoming" style={{ marginBottom: 4 }}><i />Partido abierto</span>
        <span className={`status-pill ${pill.cls}`}><i />{pill.label}</span>

        {error && <span role="alert" style={{ fontSize: 12, color: "#b91c1c" }}>{error}</span>}
        {showBlocked && (
          <span style={{ fontSize: 12, color: "#6b7280", maxWidth: 220 }}>
            {leaveBlockedMessage(me.leaveBlockedReason, { isCreator: me.isCreator })}
          </span>
        )}

        {confirming ? (
          <div role="group" aria-label="Confirmar acción" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 12, color: "#4b5563", maxWidth: 220 }}>
              {confirming === "cancel"
                ? "Se libera la cancha y avisamos a los jugadores. No se puede deshacer."
                : "Tu lugar queda libre para otro jugador."}
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                type="button"
                className="r-btn-danger"
                style={{ padding: "6px 12px", borderRadius: 9, fontSize: 12, backgroundColor: "#ef4444", color: "#fff", border: "none" }}
                disabled={busy}
                onClick={() =>
                  confirming === "cancel"
                    ? run(onCancel, "No pudimos cancelar el partido. Probá de nuevo.")
                    : run(onLeave, "No pudimos sacarte del partido. Probá de nuevo.")
                }
              >
                {busy ? <Spinner animation="border" size="sm" /> : confirming === "cancel" ? "Sí, cancelar partido" : "Sí, salir"}
              </button>
              <button type="button" className="r-btn-soft" style={{ padding: "6px 12px", borderRadius: 9, fontSize: 12 }} disabled={busy} onClick={() => setConfirming(null)}>
                No
              </button>
            </div>
          </div>
        ) : (
          <>
            {me.canCancel && (
              <button type="button" className="r-btn-soft" style={{ padding: "6px 12px", borderRadius: 9, fontSize: 12 }} onClick={() => setConfirming("cancel")}>
                Cancelar partido
              </button>
            )}
            {me.canLeave && !me.canCancel && (
              <button type="button" className="r-btn-soft" style={{ padding: "6px 12px", borderRadius: 9, fontSize: 12 }} onClick={() => setConfirming("leave")}>
                Salir del partido
              </button>
            )}
          </>
        )}
        <span className="harrow" onClick={() => onOpen(match._id)}>
          Ver partido <RIcon name="chevR" width={15} height={15} />
        </span>
      </div>
    </div>
  );
};

export default OpenMatchHistoryCard;
